import type { CSSProperties } from 'react';
import { wrapFontNamesWithQuotes } from 'common/utils/css-font-family';
import {
	getDesignPresetInheritedAssignment,
	getDesignPresetTokenId,
	normalizePlatformStyleValue,
	resolveDesignModeValue,
	resolveDesignTokenReference,
	type DesignDefinition,
	type DesignModeSelection,
	type DesignPreset,
	type DesignPresetProperty,
	type DesignPresetPropertyValue,
} from 'common/design-tokens';
import { getPlatformReferenceWidth, orderMediaQueryConfig } from 'common/utils/story-media-query';
import { convertVwToPx } from 'utils/helpers';
import type { StoryDefaultPlatform, StoryMediaQuery } from 'types/story';
import { platformsConfig } from 'admin/utils/story-media-platforms-config';
import { DEFAULT_MEDIA_QUERY_PLATFORMS } from 'common/constants/story-media-query';

const platformLabels: Record<string, string> = {
	fullHD: 'Full HD',
	desktop: 'Desktop',
	tablet: 'Tablet',
	mobileLandscape: 'Mobile landscape',
	mobile: 'Mobile',
};

export function getDesignPresetPlatformName(key: string, label?: string) {
	return !label || label === key ? platformLabels[key] ?? key : label;
}

/** Presentation order only; assignment inheritance keeps its own breakpoint order. */
export function orderDesignPresetPlatforms(mediaQuery: StoryMediaQuery, presets: DesignPreset[] = []) {
	const platforms = orderMediaQueryConfig(mediaQuery);
	const authoredPlatforms = new Set(
		presets.flatMap(preset =>
			Object.values(preset.properties).flatMap(assignment => Object.keys(assignment.platforms ?? {}))
		)
	);
	for (const key of authoredPlatforms) {
		if (!platforms.some(item => item.key === key))
			platforms.push({
				key: key as StoryDefaultPlatform,
				config: { uiName: key, minWidth: null, maxWidth: null },
			});
	}
	return platforms.sort(
		(a, b) => getPlatformReferenceWidth(mediaQuery, b.key) - getPlatformReferenceWidth(mediaQuery, a.key)
	);
}

// Standard Brand Kit preview only; stored Base stays portable across Stories.
export function getDesignPresetPreviewMediaQuery(defaultPlatform: StoryDefaultPlatform): StoryMediaQuery {
	return { defaultPlatform, config: platformsConfig[defaultPlatform] };
}
export const DESIGN_PRESET_PREVIEW_MEDIA_QUERY = getDesignPresetPreviewMediaQuery(
	DEFAULT_MEDIA_QUERY_PLATFORMS.DESKTOP
);

/** Resolve inheritance before converting: inherited vw keeps its authored platform's reference width. */
export function getDesignPresetDisplayValue({
	property,
	assignment,
	platform,
	mediaQuery,
	design,
	modeSelection,
}: {
	property: DesignPresetProperty;
	assignment: DesignPresetPropertyValue;
	platform: 'base' | StoryDefaultPlatform;
	mediaQuery: StoryMediaQuery;
	design: DesignDefinition;
	modeSelection: DesignModeSelection;
}) {
	const inherited =
		platform === 'base'
			? assignment.base === undefined
				? undefined
				: { value: assignment.base, source: 'Base' }
			: getDesignPresetInheritedAssignment(assignment, platform, mediaQuery);
	if (!inherited) return undefined;
	const token = resolveDesignTokenReference(String(inherited.value), design.tokens);
	if (getDesignPresetTokenId(inherited.value) && !token) return undefined;
	const authoredValue = token ? resolveDesignModeValue<string | number>(token.value, modeSelection) : inherited.value;
	const source = (
		inherited.source === 'Base' ? mediaQuery.defaultPlatform : inherited.source
	) as StoryDefaultPlatform;
	const cssValue =
		platform === 'base' ? authoredValue : normalizePlatformStyleValue(property, authoredValue, mediaQuery, source);
	const value =
		typeof cssValue === 'string' && cssValue.endsWith('vw')
			? `${convertVwToPx(cssValue, 2, getPlatformReferenceWidth(mediaQuery, platform === 'base' ? mediaQuery.defaultPlatform : platform))}px`
			: authoredValue;
	return { value, cssValue, authoredValue };
}

/** Preview uses every resolved assignment, with the same font-family serialization as runtime. */
export function getDesignPresetPreviewStyle({
	preset,
	...context
}: Omit<Parameters<typeof getDesignPresetDisplayValue>[0], 'property' | 'assignment'> & {
	preset: DesignPreset;
}): CSSProperties {
	const style = Object.fromEntries(
		Object.entries(preset.properties).flatMap(([property, assignment]) => {
			const display = getDesignPresetDisplayValue({
				...context,
				property: property as DesignPresetProperty,
				assignment,
			});
			return display ? [[property, display.value]] : [];
		})
	);
	const hasFontAssignment = !!preset.properties.fontFamily || !!preset.properties.fontWeight;
	if (!hasFontAssignment) return style;
	const fontFamily =
		style.fontFamily ?? resolveDesignModeValue(context.design.defaults.root.fontFamily, context.modeSelection);
	return { ...style, fontFamily: wrapFontNamesWithQuotes(String(fontFamily)) };
}
